/**
 * "Follow the melody" — musical Simon on the xylophone.
 * Didactics: auditory and working memory, sequencing.
 */

import { createSignal, onCleanup, onMount, Show } from "solid-js";
import {
  createProgressStore,
  createSimonGame,
  gameStickerId,
  type SoundKey,
} from "@melodimals/game-core";
import { Star } from "@fleet/icons";
import { InstrumentStage } from "@/features/instruments/instrument-stage";
import { useAudio } from "@/providers/audio-provider";
import { RewardPopup } from "@/features/rewards/reward-popup";
import { useRewards } from "@/features/rewards/use-rewards";
import { useAppConfig } from "@/providers/config-provider";

/** Friendly subset: 5 low xylophone bars. */
const GAME_KEYS: SoundKey[] = ["C4", "D4", "E4", "F4", "G4"];

export function SimonGame() {
  const audio = useAudio();
  const progress = createProgressStore();
  const rewards = useRewards();
  // Thresholds by CONFIGURATION (adult preset or build env). They are read on
  // mount: changing difficulty mid-game would move the goal with the
  // child playing; the change takes effect on the next entry to the module.
  const { games } = useAppConfig().config();
  const demoNoteMs = games.simonDemoNoteMs;
  const game = createSimonGame({
    keys: GAME_KEYS,
    startLength: games.simonStartLength,
    maxLength: games.simonMaxLength,
  });

  const [highlighted, setHighlighted] = createSignal<SoundKey | null>(null);
  const [phase, setPhase] = createSignal(game.state().phase);
  const [level, setLevel] = createSignal(game.state().level);
  const [won, setWon] = createSignal(false);
  let timers: ReturnType<typeof setTimeout>[] = [];

  const clearTimers = () => {
    for (const t of timers) {
      clearTimeout(t);
    }
    timers = [];
  };
  onCleanup(clearTimers);

  /** Plays the sequence lighting up bars; when it ends, the child's turn. */
  const playDemo = () => {
    clearTimers();
    setPhase("demo");
    const { sequence } = game.state();
    void audio.narrator.say("guide.listen");
    const startDelay = 900; // deja terminar al narrador
    sequence.forEach((key, i) => {
      timers.push(
        setTimeout(
          () => {
            audio.playNote("xylophone", key);
            setHighlighted(key);
            timers.push(setTimeout(() => setHighlighted(null), demoNoteMs * 0.7));
          },
          startDelay + i * demoNoteMs,
        ),
      );
    });
    timers.push(
      setTimeout(
        () => {
          game.demoFinished();
          setPhase("input");
          void audio.narrator.say("guide.your-turn");
        },
        startDelay + sequence.length * demoNoteMs,
      ),
    );
  };

  onMount(() => {
    // Wait for the stage to load the instrument before the first demo.
    void (async () => {
      await audio.loadInstrument("xylophone");
      playDemo();
    })();
  });

  const handleNote = (key: SoundKey) => {
    const result = game.handleInput(key);
    if (result === "level-up") {
      setLevel(game.state().level);
      progress.recordLevel("simon", game.state().level);
      void audio.narrator.say("praise.great");
      timers.push(setTimeout(playDemo, 1200));
    } else if (result === "retry") {
      timers.push(setTimeout(playDemo, 900));
    } else if (result === "won") {
      setWon(true);
      progress.recordLevel("simon", games.simonMaxLength);
      rewards.award(gameStickerId("simon"));
      void audio.narrator.say("praise.wow");
    }
  };

  return (
    <div class="flex h-full flex-col">
      <div class="flex items-center justify-center gap-1 pb-2" aria-hidden="true">
        <Show
          when={!won()}
          fallback={
            <>
              <Star class="h-8 w-8 fill-amber-400 text-amber-500" />
              <Star class="h-10 w-10 fill-amber-400 text-amber-500" />
              <Star class="h-8 w-8 fill-amber-400 text-amber-500" />
            </>
          }
        >
          <span
            class={`rounded-full px-4 py-1 text-sm font-bold ${phase() === "input" ? "bg-white text-emerald-700" : "bg-white/50 text-zinc-500"}`}
          >
            {"⭐".repeat(level())}
          </span>
        </Show>
      </div>
      <div class="min-h-0 flex-1" classList={{ "pointer-events-none": phase() !== "input" }}>
        <InstrumentStage
          instrument="xylophone"
          highlightedKey={highlighted()}
          onNotePlayed={handleNote}
        />
      </div>

      <RewardPopup stickerId={rewards.pending()} onDismiss={rewards.dismiss} />
    </div>
  );
}
